import React from 'react';
import { cn } from '../../../utils/cn';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import Field from '../field';
import Label from '../label';
import Description from '../description';
import { Input as HeadlessInput } from '@headlessui/react';

export default function Input({
  type = 'text',
  name = '',
  placeholder,
  label,
  description,
  required,
  min = '0',
  isForm = true,
  className,
  ...props
}) {
  const { t } = useTranslation();
  const { readOnly } = props;
  return isForm ? (
    <FormInput
      name={name}
      placeholder={placeholder}
      label={label}
      description={description}
      type={type}
      required={required}
      min={min}
      className={className}
      disabled={readOnly}
      {...props}
    />
  ) : (
    <Field>
      {label ? <Label content={label} /> : null}
      <div
        className={cn({
          'flex flex-row gap-4 items-center': type === 'number',
          'flex flex-col gap-2': type !== 'number',
        })}
      >
        <HeadlessInput
          className={cn(
            'h-10 min-h-10 px-2 max-w-90 flex-1 text-sm rounded-1 border-2',
            { 'max-w-25': type === 'number' },
            'bg-input text-foreground border-border data-[hover]:border-border-active data-[focus]:border-border-active',
            readOnly && 'bg-[white]/0 border-none p-0 items-start h-fit min-h-4',
            className
          )}
          placeholder={t(placeholder)}
          type={type}
          min={min}
          disabled={readOnly}
          {...props}
        />
        {description && <Description content={description} />}
      </div>
    </Field>
  );
}

function FormInput({
  name,
  label,
  description,
  placeholder,
  type,
  required,
  min,
  className,
  ...props
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const { readOnly } = props;
  const { t } = useTranslation();
  const errorMessage = errors[name]?.message;

  return (
    <Field>
      {label && <Label content={label} error={errorMessage} />}
      <div
        className={cn(
          type === 'number'
            ? 'flex flex-row gap-4 items-center'
            : 'flex flex-col gap-2'
        )}
      >
        <HeadlessInput
          className={cn(
            'h-10 min-h-10 px-2 max-w-90 flex-1 text-sm rounded-1 border-2',
            type === 'number' && 'min-w-25 max-w-25',
            errorMessage
              ? 'bg-input-error text-error border-error-foreground data-[hover]:border-error data-[focus]:border-error'
              : 'bg-input text-foreground border-border data-[hover]:border-border-active data-[focus]:border-border-active',
            readOnly && 'bg-[white]/0 border-none p-0 items-start h-fit min-h-4',
            className
          )}
          {...register(name, {
            required: required && t('requiredField'),
          })}
          placeholder={t(placeholder)}
          type={type}
          min={min}
          {...props}
        />
        {(description || errorMessage) && (
          <Description content={description} error={errorMessage} />
        )}
      </div>
    </Field>
  );
}
